import React from 'react';

import { capitalize } from 'lodash/fp';
import { TronWeb } from 'tronweb';

import { Badge } from '@/components/shadcn/ui/badge';
import { Currency } from '@/constants';
import useBuildInfoStore from '@/hooks/useBuildInfo';
import useTokenIconByCurrency from '@/hooks/useChainIconByCurrency';
import useDecomposeToken from '@/hooks/useDecomposeToken';
import { truncateMidAddressInputValue } from '@/utils';

import BuildDetailsCopyBadge from '../components/BuildDetailsCopyBadge';
import BuildDetailsGrid, { BuildDetailsGridLabelValuePair } from '../components/BuildDetailsGrid';

import BuildDetailsTokenInfoBeta from './BuildDetailsTokenInfoBeta';
import BuildDetailsTokenInfoL1Contract from './BuildDetailsTokenInfoL1Contract';
import BuildDetailsTokenInfoL2Contract from './BuildDetailsTokenInfoL2Contract';

const BuildDetailsTokenInfo: React.FC<{ currency: Currency }> = ({ currency }) => {
  const { buildInfo } = useBuildInfoStore();

  const { fullName, readableName, type } = useDecomposeToken(currency);
  const { icon, getChainIcon } = useTokenIconByCurrency(currency);

  const contractDetailsByCurrency = buildInfo?.[currency];

  if (!contractDetailsByCurrency) return null;

  return (
    <BuildDetailsGrid
      collapsible
      className="grid-cols-[18px_1fr] py-2"
      contentClassName="m-0.5 p-0.5 bg-background/50 rounded-xl"
      content={
        <ul className="flex flex-col gap-0.5">
          {Object.entries(contractDetailsByCurrency)
            .sort(([keyA], [keyB]) => {
              if (keyA.startsWith('BETA')) return 1;
              if (keyB.startsWith('BETA')) return -1;
              return keyA.localeCompare(keyB);
            })
            .map(([key, value]) => {
              if (key.toUpperCase() === 'NETWORK_NAME') {
                return (
                  <li key={key}>
                    <BuildDetailsGrid>
                      <BuildDetailsGridLabelValuePair
                        label={key.split('_').map(capitalize).join(' ')}
                        value={
                          <div className="flex items-center gap-1">
                            <img
                              src={getChainIcon(value as Currency)}
                              alt={value as string as Currency}
                              className="size-3"
                            />
                            {value as string}
                          </div>
                        }
                      />
                    </BuildDetailsGrid>
                  </li>
                );
              }

              if (typeof value === 'string') {
                const isValidContractId = value?.startsWith('0x') || TronWeb.isAddress(value);

                return (
                  <li key={key}>
                    <BuildDetailsGrid>
                      <BuildDetailsGridLabelValuePair
                        label={key.split('_').map(capitalize).join(' ')}
                        value={
                          isValidContractId ? (
                            <BuildDetailsCopyBadge
                              value={value}
                              maskValue={(value) => truncateMidAddressInputValue(String(value), 24)}
                            />
                          ) : (
                            value
                          )
                        }
                      />
                    </BuildDetailsGrid>
                  </li>
                );
              }

              if (key.startsWith('BETA')) {
                return (
                  <li key={key}>
                    <BuildDetailsTokenInfoBeta currency={currency} betaData={value} />
                  </li>
                );
              }

              if (typeof value === 'object') {
                if (key.startsWith('L1')) {
                  return (
                    <li key={key}>
                      <BuildDetailsTokenInfoL1Contract
                        currency={currency}
                        l1ContractProp={key}
                        l1ContractData={value}
                      />
                    </li>
                  );
                }
                if (key.startsWith('L2')) {
                  return (
                    <li key={key}>
                      <BuildDetailsTokenInfoL2Contract
                        currency={currency}
                        l2ContractProp={key}
                        l2ContractData={value}
                      />
                    </li>
                  );
                }
              }
            })}
        </ul>
      }
    >
      <div className="flex items-center gap-1">
        <img src={icon} alt={fullName} className="size-4" />
        {readableName} {fullName}
        <Badge variant="default" className="text-muted-foreground pointer-events-none p-0.5 px-1 leading-none">
          {type}
        </Badge>
      </div>
    </BuildDetailsGrid>
  );
};

export default BuildDetailsTokenInfo;
